import { Button, Dialog, LinkButton, Popover, Tooltip, useKumoToastManager } from '@cloudflare/kumo';
import { Archive, ArrowLeft, ArrowUpRight, PencilSimple, Plus, X } from '@phosphor-icons/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createRoute, getRouteApi, Link, Outlet, useNavigate, useParams } from '@tanstack/react-router';
import { lazy, Suspense, useState } from 'react';
import { z } from 'zod';
import { writable } from '../../shared/content.ts';
import { submissionActionFields } from '../../shared/form-actions.ts';
import {
  api,
  isActivationRequired,
  pageQuery,
  queryClient,
  siteQuery,
  siteSettingsQuery,
  sitesQuery,
  unwrap,
} from '../api.ts';
import { ErrorNotice, Loading, previewDialogClassName } from '../common.tsx';
import { SiteActivation } from '../components/site-activation.tsx';
import { Route as parent } from './signed-in.tsx';
export const Route = createRoute({
  getParentRoute: () => parent,
  path: '/sites/$siteId',
  validateSearch: z.object({
    locale: z.string().optional(),
    preview: z.boolean().optional(),
    changes: z.boolean().optional(),
  }).parse,
  component: SiteWorkspace,
});

const Preview = lazy(async () => ({ default: (await import('../components/site-preview.tsx')).Preview }));
const contentTabs = [
  ['/sites/$siteId/pages', 'Pages'],
  ['/sites/$siteId/posts', 'Posts'],
  ['/sites/$siteId/forms', 'Forms'],
  ['/sites/$siteId/assets', 'Assets'],
  ['/sites/$siteId/header-footer', 'Header & footer'],
] as const;
const otherTabs = [
  ['/sites/$siteId/settings', 'Settings'],
  ['/sites/$siteId/blog-automation', 'Blog automation'],
  ['/sites/$siteId/site-assistant', 'Site assistant'],
  ['/sites/$siteId/redirects', 'Redirects'],
  ['/sites/$siteId/form-entries', 'Form entries'],
  ['/sites/$siteId/reviews', 'Reviews'],
  ['/sites/$siteId/activity', 'Activity'],
  ['/sites/$siteId/checks', 'Checks'],
  ['/sites/$siteId/history', 'History'],
  ['/sites/$siteId/sharing', 'Sharing'],
] as const;
const route = getRouteApi('/_signedIn/sites/$siteId');

function SiteWorkspace() {
  const { siteId } = route.useParams();
  const search = route.useSearch();
  const sites = useQuery(sitesQuery);
  const navigate = useNavigate();
  const [previewOpen, setPreviewOpen] = useState(Boolean(search.preview));
  const { pageId, postId } = useParams({ strict: false });
  const site = useQuery(siteSettingsQuery(siteId));
  const selectedPage = useQuery({
    ...pageQuery(siteId, pageId ?? postId ?? ''),
    enabled: Boolean(pageId || postId),
  });
  const previewSnapshot = useQuery({ ...siteQuery(siteId), enabled: previewOpen || Boolean(search.preview) });

  const closePreview = () => {
    setPreviewOpen(false);
    if (search.preview)
      void navigate({ to: '.', search: (prev) => ({ ...prev, preview: undefined }), replace: true });
  };
  const [activationOpen, setActivationOpen] = useState(false);
  const toast = useKumoToastManager();
  const publish = useMutation({
    mutationFn: async () =>
      unwrap(await api.POST('/api/sites/{siteId}/publications', { params: { path: { siteId } }, body: {} })),
    onSuccess: () => {
      setActivationOpen(false);
      toast.add({ title: 'Published — your changes are live.', variant: 'success' });
      void queryClient.invalidateQueries();
    },
    onError: (error) => {
      if (isActivationRequired(error)) setActivationOpen(true);
    },
  });
  if (site.isPending) return <Loading />;
  if (site.error)
    return (
      <main className="mx-auto my-0 px-4.5 max-w-370 pt-5 pb-0 max-[768px]:[&_.workspace-heading]:flex-col max-[768px]:[&_.workspace-heading]:items-stretch max-[768px]:[&_.workspace-heading]:gap-4 min-[761px]:px-6.5 min-[761px]:pt-6 min-[761px]:pb-0 min-[1051px]:px-10 min-[1051px]:pt-7 min-[1051px]:pb-0">
        <ErrorNotice error={site.error} />
      </main>
    );
  const settings = site.data!;
  const locale =
    selectedPage.data && 'locale' in selectedPage.data
      ? selectedPage.data.locale
      : (search.locale ?? settings.baseLocale);
  const summary = sites.data?.find((entry) => entry.id === siteId);
  const siteUrl = summary?.domain ? `${location.protocol}//${summary.domain}` : undefined;
  return (
    <main className="mx-auto my-0 max-w-370 px-4.5 pb-0 min-[761px]:px-6.5 min-[1051px]:px-10">
      <div className="workspace-heading sticky z-20 pt-5 min-[761px]:pt-6 min-[1051px]:pt-7 border-b border-b-kumo-line flex flex-col items-stretch justify-between gap-5 pb-5.5 [&_h1]:m-0 [&_h1]:overflow-hidden [&_h1]:text-ellipsis [&_h1]:whitespace-nowrap [&_.action-row]:justify-start [&_.back-link]:whitespace-nowrap [&_>_div:first-child]:min-w-0 [&_>_div:first-child]:flex-1 min-[601px]:flex-row min-[601px]:items-end min-[601px]:[&_.back-link]:whitespace-normal min-[761px]:items-end">
        <div>
          <Link
            to="/"
            className="back-link inline-flex items-center gap-2 text-kumo-subtle text-[13px] mb-4 [&:hover]:text-kumo-default"
          >
            <ArrowLeft size={16} />
            My sites
          </Link>
          <h1 className="text-[20px] leading-[1.2] font-semibold tracking-[-0.35px]">
            {settings.name || 'Untitled site'}
          </h1>
        </div>
        <div className="action-row flex gap-2 items-center shrink-0 flex-nowrap max-[768px]:flex-wrap">
          {siteUrl && (
            <LinkButton href={siteUrl} variant="secondary" external disabled={!summary?.published}>
              <ArrowUpRight size={16} aria-hidden="true" />
              View site
            </LinkButton>
          )}
          <Button
            type="button"

            onClick={() => setPreviewOpen(true)}
          >
            Preview
          </Button>
          <UnpublishedChanges
            siteId={siteId}
            changeCount={summary?.unpublishedChangeCount}
            initiallyOpen={Boolean(search.changes)}
          />
          {summary?.published && summary.unpublishedChangeCount === 0 ? (
            <Tooltip
              content={
                summary.lastPublishedAt
                  ? `No changes to publish. Last published ${new Date(summary.lastPublishedAt).toLocaleString()}.`
                  : 'No changes to publish.'
              }
              render={<span className="inline-flex" tabIndex={0} />}
            >
              <Button variant="primary" type="button" disabled>
                Publish
              </Button>
            </Tooltip>
          ) : (
            <Button
              variant="primary"
              type="button"
              onClick={() => publish.mutate()}
              disabled={publish.isPending}
            >
              {publish.isPending ? 'Publishing…' : 'Publish'}
            </Button>
          )}
        </div>
      </div>
      <div className="[&:empty]:hidden">
        <ErrorNotice error={isActivationRequired(publish.error) ? undefined : publish.error} />
      </div>
      {activationOpen && summary?.ownerUserId && (
        <SiteActivation
          siteId={siteId}
          accountId={summary.ownerUserId}
          onActivated={() => publish.mutateAsync()}
          onClose={() => setActivationOpen(false)}
        />
      )}
      <Dialog.Root
        open={previewOpen || Boolean(search.preview)}
        onOpenChange={(open) => (open ? setPreviewOpen(true) : closePreview())}
      >
        <Dialog className={previewDialogClassName} aria-describedby={undefined}>
          <Suspense fallback={<Loading />}>
            <ErrorNotice error={previewSnapshot.error} />
            {previewSnapshot.data ? (
              <Preview snapshot={previewSnapshot.data} locale={locale} onClose={closePreview} />
            ) : !previewSnapshot.error ? (
              <Loading />
            ) : null}
          </Suspense>
        </Dialog>
      </Dialog.Root>
      <div className="workspace-body grid grid-cols-[180px_minmax(0,_1fr)]">
        <aside className="workspace-sidebar pt-6 pr-4 pb-8 pl-0">
          <nav
            id="site-sections"
            className="[&_a]:px-3 [&_a]:py-2.5 [&_a]:block [&_a]:rounded-[8px] [&_a]:text-[13px] [&_a]:leading-[20px] [&_a]:text-kumo-subtle [&_a]:[transition:background-color_120ms,_color_120ms] [&_a:hover]:bg-kumo-tint [&_a:hover]:text-kumo-default [&_a[aria-current=page]]:bg-app-accent-surface [&_a[aria-current=page]]:text-kumo-brand [&_a[aria-current=page]]:font-semibold"
            aria-label="Site sections"
          >
            {[contentTabs, otherTabs].map((group, index) => (
              <div
                className="workspace-nav-group grid gap-1 [&_+_.workspace-nav-group]:border-t [&_+_.workspace-nav-group]:border-t-kumo-line [&_+_.workspace-nav-group]:mt-4.5 [&_+_.workspace-nav-group]:pt-4.5"
                key={index}
              >
                {group.map(([value, label]) => (
                  <Link
                    key={value}
                    to={value}
                    params={{ siteId }}
                    search={
                      locale !== settings.baseLocale &&
                      (value === '/sites/$siteId/pages' ||
                        value === '/sites/$siteId/posts' ||
                        value === '/sites/$siteId/header-footer')
                        ? { locale }
                        : {}
                    }
                  >
                    {label}
                  </Link>
                ))}
              </div>
            ))}
          </nav>
        </aside>
        <div className="workspace-content min-w-0 border-l border-l-kumo-line pl-7">
          <Outlet />
        </div>
      </div>
    </main>
  );
}

function UnpublishedChanges({
  siteId,
  changeCount,
  initiallyOpen,
}: {
  siteId: string;
  changeCount?: number;
  initiallyOpen: boolean;
}) {
  const disabled = changeCount === 0;
  const [open, setOpen] = useState(initiallyOpen && !disabled);
  const [selectedId, setSelectedId] = useState<string>();
  const changes = useQuery({
    queryKey: ['site', siteId, 'changes'],
    queryFn: async () =>
      unwrap(await api.GET('/api/sites/{siteId}/unpublished-changes', { params: { path: { siteId } } })),
    enabled: !disabled && (open || Boolean(selectedId)),
  });
  const selected = changes.data?.find((change) => `${change.kind}:${change.objectId}` === selectedId);
  const itemName = (change: NonNullable<typeof changes.data>[number]) => {
    const draft = change.draft;
    if ('title' in draft && draft.title) return draft.title;
    if ('name' in draft && draft.name) return draft.name;
    if (draft.kind === 'FormSubmissionAction') return `${draft.config.kind} submission action`;
    return change.kind;
  };
  const label =
    typeof changeCount === 'number' && changeCount > 0
      ? `Unpublished changes (${changeCount})`
      : 'Unpublished changes';
  if (disabled)
    return (
      <Button type="button" disabled>
        {label}
      </Button>
    );
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <Popover.Trigger render={<Button type="button" />}>{label}</Popover.Trigger>
        <Popover.Content align="end" className="p-4 w-90 max-w-[calc(100vw_-_32px)] z-50">
          <Popover.Title className="sr-only">Unpublished changes</Popover.Title>
          <ErrorNotice error={changes.error} />
          {changes.isPending ? (
            <Loading />
          ) : changes.data?.length ? (
            <ul
              aria-label="Unpublished changes"
              className="m-0 p-0 list-none max-h-[min(420px,_60dvh)] overflow-y-auto [&_li]:px-0 [&_li]:py-3 [&_li]:flex [&_li]:items-center [&_li]:justify-between [&_li]:gap-4 [&_li_+_li]:border-t [&_li_+_li]:border-t-kumo-line [&_li:first-child]:pt-0 [&_li:last-child]:pb-0 [&_strong]:block [&_strong]:flex-1 [&_strong]:min-w-0 [&_strong]:text-[13px] [&_strong]:overflow-hidden [&_strong]:text-ellipsis [&_strong]:whitespace-nowrap"
            >
              {changes.data.map((change) => (
                <li key={`${change.kind}:${change.objectId}`}>
                  <span
                    className="flex shrink-0 text-kumo-subtle"
                    role="img"
                    aria-label={change.change}
                    title={change.change}
                  >
                    {change.change === 'Added' ? (
                      <Plus size={16} />
                    ) : change.change === 'Archived' ? (
                      <Archive size={16} />
                    ) : (
                      <PencilSimple size={16} />
                    )}
                  </span>
                  <strong title={itemName(change)}>{itemName(change)}</strong>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedId(`${change.kind}:${change.objectId}`);
                      setOpen(false);
                    }}
                    aria-label={`View changes for ${itemName(change)}`}
                  >
                    View
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            !changes.error && (
              <p className="leading-[1.65] text-[13px] text-kumo-subtle">No unpublished changes.</p>
            )
          )}
        </Popover.Content>
      </Popover>
      <Dialog.Root
        open={Boolean(selected)}
        onOpenChange={(open) => {
          if (!open) setSelectedId(undefined);
        }}
      >
        <Dialog className={`${previewDialogClassName} flex flex-col`} aria-describedby={undefined}>
          {selected && (
            <>
              <div className="px-6 py-5 border-b border-b-kumo-line flex justify-between items-center gap-4 [&_h2]:m-0 [&_h2]:text-[18px] [&_h2]:font-semibold [&_h2]:tracking-[-0.35px] [&_h2]:wrap-anywhere">
                <div>
                  <Dialog.Title>{itemName(selected)}</Dialog.Title>
                  <span className="block text-[11px] text-kumo-subtle mt-[3px]">{selected.change}</span>
                </div>
                <Button aria-label="Close change details" onClick={() => setSelectedId(undefined)}>
                  <X size={20} />
                </Button>
              </div>
              <div className="grid grid-cols-2 flex-1 min-h-0 overflow-auto [&_section]:p-6 [&_section]:min-w-0 [&_section_+_section]:border-l [&_section_+_section]:border-l-kumo-line [&_h3]:mx-0 [&_h3]:mt-0 [&_h3]:mb-4 [&_h3]:text-[13px] [&_h3]:font-semibold [&_h3]:text-kumo-subtle [&_pre]:text-[12px] [&_pre]:leading-[1.7] [&_pre]:whitespace-pre-wrap [&_pre]:wrap-anywhere max-[768px]:grid-cols-1 max-[768px]:[&_section_+_section]:border-t max-[768px]:[&_section_+_section]:border-l-0 max-[768px]:[&_section_+_section]:border-t-kumo-line max-[768px]:[&_section_+_section]:border-l-current">
                <section>
                  <h3 className="text-[16px] font-semibold">Published</h3>
                  <pre>
                    {selected.published
                      ? JSON.stringify(
                          selected.published.kind === 'FormSubmissionAction'
                            ? submissionActionFields(selected.published)
                            : writable(selected.published),
                          null,
                          2,
                        )
                      : 'Not published'}
                  </pre>
                </section>
                <section>
                  <h3 className="text-[16px] font-semibold">Draft</h3>
                  <pre>
                    {selected.change === 'Archived'
                      ? 'Archived'
                      : JSON.stringify(
                          selected.draft.kind === 'FormSubmissionAction'
                            ? submissionActionFields(selected.draft)
                            : writable(selected.draft),
                          null,
                          2,
                        )}
                  </pre>
                </section>
              </div>
            </>
          )}
        </Dialog>
      </Dialog.Root>
    </>
  );
}
